/**
 * Idle session watcher.
 *
 * Mounted once inside the auth provider. While a user is logged in, it runs the
 * idle timers and, at the warning threshold, shows a modal with a live 60s
 * countdown: "Stay signed in" resets the timer, "Log out now" (or the countdown
 * hitting zero) ends the session.
 *
 * Timings are deliberately module constants, not props — session policy should
 * be defined in one place, not passed around.
 *
 * Sep-3 (v1): added SESSION_MAX_MS + isSessionExpired for wall-clock expiry
 * that survives laptop sleep.
 *
 * Sep-3 (v2): added isAbsoluteSessionExpired — a "cap-only" variant that
 * skips the idle check. Used on RESTORE (page refresh), where the idle
 * check produced false positives: a user reading a page for 15+ min
 * without input would refresh and get kicked out. The absolute cap alone
 * still catches "closed laptop overnight" via SESSION_MAX_MS. Wake handlers
 * (visibility / focus) keep using the full isSessionExpired.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { useAuth } from "./AuthContext";
import { useIdleTimeout } from "./hooks/useIdleTimeout";
import { ConfirmDialog } from "@shared/ui";
import { getLastActivity, getSessionStart } from "@shared/api";
import styles from "./IdleSessionWatcher.module.scss";

/** 15 min total; warn at 14 min (60s to respond). */
export const IDLE_MS = 15 * 60_000;
export const WARN_MS = 60_000;

/** Absolute session cap — even a continuously-active user is signed out
 *  after this. 8 hours matches a typical shift; tune to your policy.
 *  This is the second line of defence behind IDLE_MS. */
export const SESSION_MAX_MS = 8 * 60 * 60_000;

/** FULL wall-clock check — used by the wake handlers (visibility change /
 *  window focus) in AuthContext. Returns true if EITHER cap is breached:
 *  session too old, OR user hasn't touched anything in > IDLE_MS. */
export function isSessionExpired(now: number = Date.now()): boolean {
  const startedAt = getSessionStart();
  if (startedAt !== null && now - startedAt > SESSION_MAX_MS) return true;
  const lastActivity = getLastActivity();
  if (lastActivity !== null && now - lastActivity > IDLE_MS) return true;
  return false;
}

/** ABSOLUTE-ONLY check — used on RESTORE (fresh page load). Skips the
 *  idle check because it produced false positives:
 *    - user reads a page for > IDLE_MS without input events
 *    - user refreshes → lastActivity is stale → forced logout
 *  The absolute cap (8h since login) still catches overnight sleep and
 *  long-abandoned sessions, without punishing the "read a page and
 *  refresh" case. In-app useIdleTimeout continues to enforce idle
 *  behaviour while the app is running. */
export function isAbsoluteSessionExpired(now: number = Date.now()): boolean {
  const startedAt = getSessionStart();
  if (startedAt !== null && now - startedAt > SESSION_MAX_MS) return true;
  return false;
}

export const IdleSessionWatcher: React.FC = () => {
  const { user, signOut } = useAuth();
  const [warning, setWarning] = useState(false);
  const [remaining, setRemaining] = useState(WARN_MS / 1000);
  const countdown = useRef<number | undefined>(undefined);

  const stopCountdown = useCallback(() => {
    window.clearInterval(countdown.current);
    countdown.current = undefined;
  }, []);

  const logout = useCallback(() => {
    stopCountdown();
    setWarning(false);
    void signOut();
  }, [signOut, stopCountdown]);

  const stayIn = useCallback(() => {
    stopCountdown();
    setWarning(false);
  }, [stopCountdown]);

  // When the warning opens, run a 1s countdown; hitting zero logs out.
  useEffect(() => {
    if (!warning) return;
    setRemaining(WARN_MS / 1000);
    countdown.current = window.setInterval(() => {
      setRemaining((r) => {
        if (r <= 1) {
          // Defer the logout out of the state updater.
          window.setTimeout(logout, 0);
          return 0;
        }
        return r - 1;
      });
    }, 1000);
    return stopCountdown;
  }, [warning, logout, stopCountdown]);

  useIdleTimeout({
    idleMs: IDLE_MS,
    warnMs: WARN_MS,
    enabled: user !== null,
    onWarn: () => setWarning(true),
    onIdle: logout,
    onActive: stayIn,
  });

  if (!warning || user === null) return null;

  return (
    <ConfirmDialog
      tone="warn"
      title="Are you still there?"
      text={`You'll be signed out in <b>${remaining}s</b> due to inactivity.`}
      confirmLabel="Stay signed in"
      // Escape / backdrop / × and the confirm button all resolve to "stay":
      // dismissing an "are you there?" prompt means yes. Logout happens only via
      // the explicit button below or when the countdown reaches zero.
      onConfirm={stayIn}
      onCancel={stayIn}
    >
      <button type="button" className={styles.logoutNow} onClick={logout}>
        Log out now
      </button>
    </ConfirmDialog>
  );
};
